import { useWeb3React } from '@web3-react/core'
import Zksync from 'assets/png/Zksync.png'
import bera from 'assets/png/bera.png'
import bsc from 'assets/png/bsc.png'
import swapWhite from 'assets/svg/swap-white.svg'
import { ButtonV3 } from 'components/Button/ButtonV3'
import { useCallback, useEffect, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { ChainModal } from './ChainModal'
import { BrigeForm } from './bridge'
import { BERA_OFT, BSC_OFT, ZKSYNC_OFT } from './constant'

export function Chain() {
  const { control, setValue } = useFormContext<BrigeForm>()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalType, setModalType] = useState<'from' | 'to'>('from')

  const [from, to] = useWatch({ control, name: ['from', 'to'] })
  const { chainId } = useWeb3React()

  const openChainModal = useCallback((type: 'from' | 'to') => {
    setModalType(type)
    setIsModalOpen(true)
  }, [])

  const switchBridge = useCallback(() => {
    // Allowed routes: zkSync → BSC, Bera → zkSync
    // Blocked routes: zkSync → Bera, Bera → BSC, BSC → Bera, BSC → zkSync

    if (from.chainId === ZKSYNC_OFT.chainId && to.chainId === BSC_OFT.chainId) {
      // zkSync → BSC: switch to Bera → zkSync
      setValue('from', {
        chainId: BERA_OFT.chainId,
        name: BERA_OFT.name,
        img: bera,
      })
      setValue('to', {
        chainId: ZKSYNC_OFT.chainId,
        name: ZKSYNC_OFT.name,
        img: Zksync,
      })
    } else if (from.chainId === BERA_OFT.chainId && to.chainId === ZKSYNC_OFT.chainId) {
      // Bera → zkSync: switch to zkSync → BSC
      setValue('from', {
        chainId: ZKSYNC_OFT.chainId,
        name: ZKSYNC_OFT.name,
        img: Zksync,
      })
      setValue('to', {
        chainId: BSC_OFT.chainId,
        name: BSC_OFT.name,
        img: bsc,
      })
    } else {
      // Default: set to zkSync → BSC
      setValue('from', {
        chainId: ZKSYNC_OFT.chainId,
        name: ZKSYNC_OFT.name,
        img: Zksync,
      })
      setValue('to', {
        chainId: BSC_OFT.chainId,
        name: BSC_OFT.name,
        img: bsc,
      })
    }
    setValue('value', '')
  }, [from.chainId, to.chainId, setValue])

  useEffect(() => {
    async function detectChainId() {
      // Set appropriate bridge pairs based on connected chain
      // Allowed routes: zkSync → BSC, zkSync → Bera, Bera → zkSync

      if (chainId === ZKSYNC_OFT.chainId) {
        // Default to zkSync → BSC when connected to zkSync
        setValue('from', {
          chainId: ZKSYNC_OFT.chainId,
          name: ZKSYNC_OFT.name,
          img: Zksync,
        })
        setValue('to', {
          chainId: BSC_OFT.chainId,
          name: BSC_OFT.name,
          img: bsc,
        })
      } else if (chainId === BERA_OFT.chainId) {
        // Only allow Bera → zkSync when connected to Berachain
        setValue('from', {
          chainId: BERA_OFT.chainId,
          name: BERA_OFT.name,
          img: bera,
        })
        setValue('to', {
          chainId: ZKSYNC_OFT.chainId,
          name: ZKSYNC_OFT.name,
          img: Zksync,
        })
      } else {
        // Default for other chains (including BSC): show zkSync → BSC
        // Users need to switch to zkSync first for BSC bridging
        setValue('from', {
          chainId: ZKSYNC_OFT.chainId,
          name: ZKSYNC_OFT.name,
          img: Zksync,
        })
        setValue('to', {
          chainId: BSC_OFT.chainId,
          name: BSC_OFT.name,
          img: bsc,
        })
      }
      setValue('value', '')
    }

    detectChainId()
  }, [chainId, setValue])

  return (
    <>
      <div className="relative bg-[#141414] rounded-2xl flex flex-col lg:flex-row lg:items-center lg:gap-6 overflow-hidden">
        <button
          onClick={() => openChainModal('from')}
          className="flex-1 p-4 lg:p-5 bg-[#141414] flex items-center gap-3 hover:bg-[#1a1a1a] transition-colors cursor-pointer"
        >
          <div className="w-10 lg:w-11 h-10 lg:h-11 flex-shrink-0">
            <img
              src={from.img}
              className="w-full h-full object-cover rounded-full"
              alt="From chain"
              width={20}
              height={20}
            />
          </div>

          <div className="px-0.5 flex flex-col gap-1">
            <p className="text-[#808080] text-sm">From</p>
            <p className="text-white text-base font-medium line-clamp-1">{from.name}</p>
          </div>
        </button>

        <ButtonV3
          // disabled
          onClick={switchBridge}
          className="absolute right-6 lg:right-auto top-1/2 lg:top-auto -translate-y-1/2 lg:translate-y-0 lg:relative  w-9 h-9 p-2 bg-[#191919] rounded-xl border border-[#1f1f1f] flex justify-center items-center gap-2.5"
        >
          <img
            src={swapWhite}
            alt="swapWhite"
            width={20}
            height={20}
            loading="lazy"
            className="rotate-90 lg:rotate-0"
          />
        </ButtonV3>

        <button
          onClick={() => openChainModal('to')}
          className="flex-1 p-4 lg:p-5 bg-[#141414] justify-end items-center gap-3 flex flex-row-reverse lg:flex-row hover:bg-[#1a1a1a] transition-colors cursor-pointer"
        >
          <div className="px-0.5 flex lg:items-end flex-col gap-1">
            <p className="text-[#808080] text-sm">To</p>
            <p className="text-white text-base font-medium line-clamp-1">{to.name}</p>
          </div>

          <div className="w-10 lg:w-11 h-10 lg:h-11 flex-shrink-0">
            <img
              src={to.img}
              className="w-full h-full object-cover rounded-full"
              alt="To chain"
              width={20}
              height={20}
            />
          </div>
        </button>
      </div>

      <ChainModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        type={modalType}
        currentFromChain={from.chainId}
        currentToChain={to.chainId}
      />
    </>
  )
}
